import withSharedStyles from "../theme/Shared.style";

const withCustomStyles = withSharedStyles(theme => {
  const { color, radius, shadow, control, fontSize } = theme.tokens;
  const { layout } = theme;
  const pillLink = {
    height: control.md,
    padding: "0 18px",
    borderRadius: radius.pill,
    display: "flex",
    alignItems: "center",
    flex: "none",
    fontSize: fontSize.body,
    fontWeight: 600,
    textDecoration: "none",
    whiteSpace: "nowrap",
    boxSizing: "border-box"
  };
  const plainButton = {
    appearance: "none",
    border: 0,
    margin: 0,
    background: "none",
    font: "inherit",
    color: "inherit",
    cursor: "pointer"
  };

  return {
    appBar: {
      zIndex: 11,
      width: "100%",
      maxWidth: "100%",
      padding: 0,
      backgroundColor: color.bg,
      color: color.ink,
      boxShadow: shadow.bar
    },
    bar: {
      ...theme.mixins.container,
      position: "relative",
      zIndex: 9999,
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      columnGap: 24,
      rowGap: 12,
      paddingTop: 14,
      paddingBottom: 14,
      // this key replaces the container mixin's phone block, so restate the
      // phone gutter here or the header stays 32px while pages use 16px
      [theme.media.phone]: {
        flexWrap: "wrap",
        columnGap: 4,
        rowGap: 8,
        paddingLeft: layout.gutterMobile,
        paddingRight: layout.gutterMobile,
        paddingTop: 8,
        paddingBottom: 8
      }
    },
    logoLink: {
      display: "flex",
      flex: "none",
      [theme.media.phone]: { marginRight: 4 }
    },
    logo: {
      height: 40,
      width: "auto",
      display: "block",
      [theme.media.phone]: { height: 26 }
    },
    locationWrap: {
      flex: "0 1 auto",
      minWidth: 0,
      // phones: location absorbs the leftover width so the cart never wraps
      [theme.media.phone]: { flex: "1 1 0" }
    },
    locationBtn: {
      ...plainButton,
      display: "flex",
      alignItems: "center",
      gap: 8,
      height: control.md,
      padding: "0 4px",
      maxWidth: "100%",
      fontSize: fontSize.body,
      fontWeight: 600,
      whiteSpace: "nowrap",
      [theme.media.phone]: {
        gap: 4,
        height: control.icon,
        padding: 0,
        fontSize: fontSize.small
      },
      "&:hover": { color: color.brandText }
    },
    etaDot: { color: color.muted, flex: "none" },
    eta: {
      fontWeight: 700,
      flex: "none",
      [theme.media.phone]: { display: "none" }
    },
    pinIcon: {
      fontSize: theme.tokens.iconSize.md,
      flex: "none",
      [theme.media.phone]: { fontSize: theme.tokens.iconSize.sm }
    },
    locationText: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      minWidth: 0,
      maxWidth: 320,
      [theme.media.phone]: { maxWidth: "none" }
    },
    chevron: {
      fontSize: theme.tokens.iconSize.sm,
      flex: "none",
      transition: "transform .2s ease-in-out"
    },
    chevronOpen: {
      transform: "rotate(180deg)"
    },
    search: {
      flex: "1 1 280px",
      minWidth: 240,
      // 720–1099: search wraps to its own row under the header
      [theme.media.belowDesktop]: {
        order: 10,
        flexBasis: "100%"
      }
    },
    spacer: {
      flex: 1
    },
    cartBtn: {
      position: "relative",
      width: control.md,
      height: control.md,
      borderRadius: radius.pill,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      color: color.ink,
      "&:hover": { backgroundColor: color.field },
      // search moves to its own row below 1100, so push cart + account right
      [theme.media.belowDesktop]: { marginLeft: "auto" },
      [theme.media.phone]: {
        width: control.icon,
        height: control.icon
      }
    },
    // phones only: replaces the old hamburger; desktop uses authActions
    phoneAccountBtn: {
      ...plainButton,
      display: "none",
      width: control.icon,
      height: control.icon,
      padding: 0,
      flex: "none",
      alignItems: "center",
      // avatar's right edge sits on the gutter, tap target stays 44px
      justifyContent: "flex-end",
      color: color.ink,
      [theme.media.phone]: { display: "flex" }
    },
    personIcon: {
      fontSize: theme.tokens.iconSize.xl,
      marginRight: -2
    },
    cartIcon: {
      fontSize: theme.tokens.iconSize.lg
    },
    cartCount: {
      position: "absolute",
      top: 2,
      right: 0,
      minWidth: 20,
      height: 20,
      padding: "0 5px",
      boxSizing: "border-box",
      borderRadius: radius.pill,
      backgroundColor: color.ink,
      color: color.bg,
      fontSize: fontSize.badge,
      fontWeight: 800,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: `2px solid ${color.bg}`
    },
    authActions: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      flex: "none",
      // phones use the drawer for account links
      [theme.media.phone]: { display: "none" }
    },
    loginBtn: {
      ...pillLink,
      color: color.ink,
      "&:hover": { backgroundColor: color.field }
    },
    signupBtn: {
      ...pillLink,
      padding: "0 22px",
      fontWeight: 700,
      backgroundColor: color.ink,
      color: color.bg,
      "&:hover": { backgroundColor: color.inkHover, color: color.bg }
    },
    accountBtn: {
      ...plainButton,
      height: control.md,
      padding: "0 12px 0 4px",
      borderRadius: radius.pill,
      display: "flex",
      alignItems: "center",
      gap: 10,
      fontSize: fontSize.body,
      fontWeight: 600,
      "&:hover": { backgroundColor: color.field }
    },
    avatar: {
      width: 40,
      height: 40,
      borderRadius: radius.pill,
      backgroundColor: color.ink,
      color: color.bg,
      fontSize: fontSize.ui,
      fontWeight: 800,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    avatarSm: {
      width: 34,
      height: 34,
      fontSize: fontSize.small
    },
    menuPaper: {
      width: 260,
      marginTop: 8,
      padding: 8
    },
    menuList: {
      padding: 0
    },
    menuHead: {
      listStyle: "none",
      padding: "12px 14px 10px",
      display: "flex",
      flexDirection: "column",
      gap: 2
    },
    menuName: {
      fontSize: fontSize.body,
      fontWeight: 800
    },
    menuPhone: {
      fontSize: fontSize.small,
      color: color.muted
    },
    menuDivider: {
      margin: "4px 6px"
    },
    menuItem: {
      minHeight: control.icon,
      padding: "12px 14px",
      borderRadius: radius.field,
      fontSize: fontSize.body,
      fontWeight: 600,
      color: color.ink,
      "&:hover, &.Mui-focusVisible": { backgroundColor: color.surface }
    },
    menuDanger: {
      color: color.danger,
      "&:hover, &.Mui-focusVisible": { backgroundColor: color.dangerTint }
    },

    // Location welcome popper (first visit, no location yet)
    popperUi: {
      zIndex: 99999,
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      minWidth: "300px",
      maxWidth: "520px",
      top: "60px",
      borderRadius: radius.card
    },
    locationModalWrap: {
      padding: "20px 24px 24px",
      boxShadow: `${shadow.overlay}, ${shadow.hairline}`
    },
    dimBackground: {
      width: "100%",
      height: "100%",
      position: "fixed",
      backgroundColor: color.backdrop,
      top: 0,
      left: 0,
      zIndex: 2
    },
    paddingRight1: {
      paddingRight: theme.spacing(1)
    },
    marginBottom0: {
      marginBottom: 0
    },
    btnLoading: {
      position: "absolute",
      width: "20px !important",
      height: "20px !important"
    },
    deliveryBtn: {
      padding: "0 16px"
    },

    // Saved-address dropdown under the location button
    locationDropDown: {
      position: "absolute",
      top: "100%",
      left: layout.gutter + 120,
      zIndex: 9999,
      width: 450,
      maxWidth: `calc(100% - ${layout.gutterMobile * 2}px)`,
      maxHeight: 450,
      marginTop: 4,
      overflow: "hidden",
      overflowY: "auto",
      borderRadius: radius.card,
      backgroundColor: color.bg,
      boxShadow: shadow.overlay,
      "&:empty": { boxShadow: "none" },
      [theme.media.phone]: {
        left: layout.gutterMobile
      }
    },
    loader: {
      top: "45vh",
      position: "absolute",
      zIndex: 2
    }
  };
});

export default withCustomStyles;
